import React from 'react';
import { CheckCircle2Icon, MapPinIcon, QrCodeIcon, StarIcon } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { easeEnter } from '../../utils/motion';
type CatchVisualProps = {
  active: number;
};
const screens = [{
  eyebrow: 'CAST',
  title: 'Something big is swimming nearby.',
  body: 'A product moment opens in a chosen place, for a chosen audience.',
  icon: MapPinIcon
}, {
  eyebrow: 'CATCH',
  title: 'You caught a Kexchoklad.',
  body: 'The consumer claims the product themselves — participation, not a handout.',
  icon: StarIcon
}, {
  eyebrow: 'REDEEM',
  title: 'Show this at the counter.',
  body: 'The catch moves through a simple partner redemption flow.',
  icon: QrCodeIcon
}, {
  eyebrow: 'FEEDBACK',
  title: 'How was it?',
  body: 'The experience returns a signal the next campaign can use.',
  icon: CheckCircle2Icon
}];

/**
 * The pinned phone for Gone Fishing. The device stays put while the catch →
 * redeem → feedback story advances inside it.
 */
export function CatchVisual({
  active
}: CatchVisualProps) {
  const reduceMotion = useReducedMotion();
  const screen = screens[Math.min(active, screens.length - 1)];
  const Icon = screen.icon;
  return <div className="mx-auto w-full max-w-[300px]" data-id="element-673" data-mp="1sgcc3ubvvndi">
      <div className="rounded-[42px] border-[7px] border-ink bg-water p-2 shadow-[14px_14px_0_#11110f]" data-id="element-674" data-mp="1serkodkv8kjv">
        <div className="min-h-[430px] rounded-[32px] bg-water-light p-5 text-paper" data-id="element-675" data-mp="1efjl3jksjaf5">
          <div className="mx-auto h-5 w-20 rounded-full bg-ink" data-id="element-676" data-mp="1ijzl547q3nmg" />

          <AnimatePresence mode="wait" data-id="element-677" data-mp="14jghsae7d5qm">
            <motion.div key={screen.eyebrow} initial={reduceMotion ? false : {
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduceMotion ? undefined : {
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.24,
            ease: easeEnter
          }} data-id="element-678" data-mp="14khraafklk68">
              <p className="mt-8 text-xs font-extrabold tracking-[0.14em] text-lagoon" data-id="element-679" data-mp="2nimp2hciyyp1">
                {screen.eyebrow}
              </p>
              <p className="mt-3 font-display text-3xl font-bold leading-[0.95]" data-id="element-680" data-mp="3l6y0oi20y65w">
                {screen.title}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-paper/70" data-id="element-681" data-mp="3beiflbvfl1gs">
                {screen.body}
              </p>

              <div className="mt-8 grid h-40 place-items-center rounded-[24px] border border-lagoon/40" data-id="element-682" data-mp="36ffkaj5969m0">
                <Icon size={44} className="text-lagoon" aria-hidden="true" data-id="element-683" data-mp="7rruzbtcnbj3" />
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-6 flex items-center gap-2" data-id="element-684" data-mp="b84k79wo3623">
            {screens.map((item, index) => <span key={item.eyebrow} className={`h-1 flex-1 transition-colors duration-200 ${index <= active ? 'bg-signal' : 'bg-paper/25'}`} data-id="element-685" data-mp="f9g89azw7j86" data-mpi={index} />)}
          </div>
        </div>
      </div>
      <p className="mt-5 text-center text-xs font-bold text-ink/55" data-id="element-686" data-mp="p5jcqni7vtya">
        ILLUSTRATIVE CATCH EXPERIENCE
      </p>
    </div>;
}